import * as React from 'react'
import { useMemo, useRef, useState } from 'react'

import { useDividendAccrualList } from './hooks'
import { DataIndex, DividendAccrual } from './types'
import { CButton, CCardBody } from '@coreui/react-pro'
import { Button, Flex, Input, InputRef, Space, Table, Typography } from 'antd'
import { path } from '../../../../../libs'
import { SearchOutlined } from '@ant-design/icons'
import { FilterConfirmProps } from 'antd/es/table/interface'
import Highlighter from 'react-highlight-words'
import { ColumnsType } from 'antd/lib/table'
import { getFinsystemNumberFormat } from '../../../../../libs/utils'

export const UserAccrualList = () => {
  const [searchText, setSearchText] = useState('')
  const [searchedColumn, setSearchedColumn] = useState('')
  const searchInput = useRef<InputRef>(null)
  const { dividendAccruals, loading } = useDividendAccrualList()

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: DataIndex,
  ) => {
    confirm()
    setSearchText(selectedKeys[0])
    setSearchedColumn(dataIndex)
  }

  const handleReset = (clearFilters: () => void) => {
    clearFilters()
    setSearchText('')
  }

  const getCSV = () => {
    if (dividendAccruals.userDividendAccruals.length == 0) {
      return 'data:text/csv;charset=utf-8,'
    }

    const csvContent = dividendAccruals.userDividendAccruals.map((item) => {
      return {
        ID: `${item.userIdOrAddress}`,
        Tokens: item.tokenBalance,
        'Amount GROSS': `${getFinsystemNumberFormat(item.amountGross, 2)}`,
        'Amount FEE': `${getFinsystemNumberFormat(item.amountFee, 2)}`,
        'Amount NEW': `${getFinsystemNumberFormat(item.amountNet, 2)}`,
      }
    })

    const header = Object.keys(csvContent[0])?.join(',')
    const data = csvContent.map((item) => Object.values(item).join(','))

    return (
      'data:text/csv;charset=utf-8, ' +
      encodeURIComponent([header, ...data].join('\n'))
    )
  }

  const getColumnSearchProps = (
    dataIndex: DataIndex,
  ): {
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }: {
      setSelectedKeys: any
      selectedKeys: any
      confirm: any
      clearFilters?: any
      close: any
    }) => JSX.Element
    filterIcon: (filtered: boolean) => JSX.Element
    onFilter: (value: any, record: any) => boolean
    render: (text: any) => any
    onFilterDropdownOpenChange: (visible: any) => void
  } => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close()
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100)
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  })

  const columns: ColumnsType<DividendAccrual> = [
    {
      title: 'UserID',
      dataIndex: 'userIdOrAddress',
      sorter: (a, b) => +a.userIdOrAddress - +b.userIdOrAddress,
      ...getColumnSearchProps('userIdOrAddress'),
      render: (item) => (
        <a href={path.clients.details(item)} target="_blank">
          {item}
        </a>
      ),
    },
    {
      title: 'Tokens',
      dataIndex: 'tokenBalance',
      sorter: (a, b) => +a.tokenBalance - +b.tokenBalance,
    },
    {
      title: 'Amount GROSS',
      dataIndex: 'amountGross',
      render: (item) => <div>{item + ' USD'}</div>,
    },
    {
      title: 'Amount FEE',
      dataIndex: 'amountFee',
      render: (item) => <div>{item + ' USD'}</div>,
    },
    {
      title: 'Amount NET',
      dataIndex: 'amountNet',
      render: (item) => <div>{item + ' USD'}</div>,
    },
  ]

  const usersSum = useMemo(() => {
    return {
      userIDs: dividendAccruals.userDividendAccruals.length,
      tokens: dividendAccruals.userDividendAccruals.reduce(
        (accum, current) => accum + Number(current.tokenBalance),
        0,
      ),
      amountGross: dividendAccruals.userDividendAccruals.reduce(
        (accum, current) => accum + Number(current.amountGross),
        0,
      ),
      amountFee: dividendAccruals.userDividendAccruals.reduce(
        (accum, current) => accum + Number(current.amountFee),
        0,
      ),
      amountNet: dividendAccruals.userDividendAccruals.reduce(
        (accum, current) => accum + Number(current.amountNet),
        0,
      ),
    }
  }, [dividendAccruals.userDividendAccruals])

  return (
    <CCardBody>
      <Flex justify="space-between">
        <h3>User tokens</h3>
        <CButton
          color="primary"
          className="mb-2"
          href={getCSV()}
          download="coreui-table-data.csv"
          target="_blank"
        >
          Download user tokens (.csv)
        </CButton>
      </Flex>
      <Flex className="mb-3" gap={50}>
        <Flex vertical>
          <Typography.Text
            style={{ fontSize: '16px', textWrap: 'nowrap' }}
            type="secondary"
          >
            UserID
          </Typography.Text>
          <Typography.Text style={{ fontSize: '16px' }}>
            {usersSum.userIDs}
          </Typography.Text>
        </Flex>
        <Flex vertical>
          <Typography.Text
            style={{ fontSize: '16px', textWrap: 'nowrap' }}
            type="secondary"
          >
            Tokens
          </Typography.Text>
          <Typography.Text style={{ fontSize: '16px' }}>
            {usersSum.tokens}
          </Typography.Text>
        </Flex>
        <Flex vertical>
          <Typography.Text
            style={{ fontSize: '16px', textWrap: 'nowrap' }}
            type="secondary"
          >
            Amount GROSS
          </Typography.Text>
          <Typography.Text style={{ fontSize: '16px' }}>
            {getFinsystemNumberFormat(usersSum.amountGross, 2)}
          </Typography.Text>
        </Flex>
        <Flex vertical>
          <Typography.Text
            style={{ fontSize: '16px', textWrap: 'nowrap' }}
            type="secondary"
          >
            Amount FEE
          </Typography.Text>
          <Typography.Text style={{ fontSize: '16px' }}>
            {getFinsystemNumberFormat(usersSum.amountFee, 2)}
          </Typography.Text>
        </Flex>
        <Flex vertical>
          <Typography.Text
            style={{ fontSize: '16px', textWrap: 'nowrap' }}
            type="secondary"
          >
            Amount NET
          </Typography.Text>
          <Typography.Text style={{ fontSize: '16px' }}>
            {getFinsystemNumberFormat(usersSum.amountNet, 2)}
          </Typography.Text>
        </Flex>
      </Flex>

      <Table
        dataSource={dividendAccruals.userDividendAccruals}
        bordered={true}
        scroll={{ y: 500 }}
        loading={loading}
        rowKey="userIdOrAddress"
        columns={columns}
        pagination={false}
        onRow={(record, rowIndex) => {
          return {
            onClick: (event) => {},
          }
        }}
      />
    </CCardBody>
  )
}
